import { Fragment, useState } from "react";
import { useMessages } from "./i18n";
import type { IncidentProposal } from "../../server/src/shared-types";
import type { TimelineItem } from "./timeline";

const URL_PATTERN = /(https?:\/\/[^\s)]+)/g;

/** Plain text with line breaks and clickable links (opened outside Teams). */
export function RichText({ text }: { text: string }) {
  return (
    <>
      {text.split("\n").map((line, i) => (
        <Fragment key={i}>
          {i > 0 && <br />}
          {line.split(URL_PATTERN).map((part, j) =>
            j % 2 === 1 ? (
              <a key={j} href={part} target="_blank" rel="noreferrer">
                {part}
              </a>
            ) : (
              <Fragment key={j}>{part}</Fragment>
            ),
          )}
        </Fragment>
      ))}
    </>
  );
}

export function PriorityBadge({ priority }: { priority: IncidentProposal["priority"] }) {
  const t = useMessages();
  return <span className={`badge badge-p${priority}`}>{`P${priority} · ${t.priority[priority]}`}</span>;
}

export function ConfirmationCard({
  item,
  active,
  busy,
  onApprove,
  onReject,
}: {
  item: Extract<TimelineItem, { type: "confirmation" }>;
  active: boolean;
  busy: boolean;
  onApprove: () => void;
  onReject: (note: string) => void;
}) {
  const [adjusting, setAdjusting] = useState(false);
  const [note, setNote] = useState("");
  const { proposal, outcome } = item;
  const t = useMessages();

  return (
    <article className={`card ${outcome ? "card-resolved" : ""}`} aria-label={t.card.label}>
      <header className="card-header">
        <span className="card-eyebrow">{t.card.eyebrow}</span>
        <PriorityBadge priority={proposal.priority} />
      </header>
      <h3 className="card-title">{proposal.title}</h3>
      <dl className="card-fields">
        <dt>{t.card.system}</dt>
        <dd>{proposal.affected_system}</dd>
        <dt>{t.card.impact}</dt>
        <dd>{proposal.impact}</dd>
        <dt>{t.card.started}</dt>
        <dd>{proposal.started_at}</dd>
        <dt>{t.card.workaround}</dt>
        <dd>{proposal.workaround}</dd>
      </dl>
      <p className="card-summary">{proposal.summary}</p>
      <p className="card-rationale">
        <strong>{t.card.why(`P${proposal.priority}`)}</strong> {proposal.priority_rationale}
      </p>

      {outcome ? (
        <p className={`card-outcome outcome-${outcome}`}>{t.card.outcome[outcome]}</p>
      ) : active && adjusting ? (
        <form
          className="card-adjust"
          onSubmit={(e) => {
            e.preventDefault();
            onReject(note.trim());
          }}
        >
          <label htmlFor={`note-${item.id}`}>{t.card.adjustQuestion}</label>
          <textarea
            id={`note-${item.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder={t.card.adjustPlaceholder}
            autoFocus
          />
          <div className="card-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setAdjusting(false)} disabled={busy}>
              {t.card.back}
            </button>
            <button type="submit" className="btn btn-secondary" disabled={busy}>
              {t.card.sendAdjustment}
            </button>
          </div>
        </form>
      ) : active ? (
        <div className="card-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setAdjusting(true)} disabled={busy}>
            {t.card.adjust}
          </button>
          <button type="button" className="btn btn-primary" onClick={onApprove} disabled={busy}>
            {busy ? t.card.opening : t.card.open}
          </button>
        </div>
      ) : null}
    </article>
  );
}

export function IncidentLink({ item }: { item: Extract<TimelineItem, { type: "incident" }> }) {
  const t = useMessages();
  return (
    <a className="incident-link" href={item.url} target="_blank" rel="noreferrer">
      <span className="incident-icon" aria-hidden>
        {item.action === "created" ? "＋" : "↻"}
      </span>
      <span>
        <span className="incident-action">
          {item.action === "created" ? t.incident.created : t.incident.commented} {item.identifier}
        </span>
        <span className="incident-title">{item.title}</span>
      </span>
    </a>
  );
}

export function TypingIndicator() {
  const t = useMessages();
  return (
    <div className="typing" role="status" aria-label={t.typing}>
      <span />
      <span />
      <span />
    </div>
  );
}
